/**
 * AI Operator — the autonomous mode.
 *
 * The design constraint: a merchant must never wonder what the operator did to
 * their money. So the two modes are labelled by consequence, not by cleverness
 * ("proposes changes" / "applies changes"), autonomous mode is opt-in per run
 * rather than a sticky toggle, and the action ledger shows every applied change
 * with its revert window still visible.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, fmt, ApiError, type OperatorAction } from '../api';

type Mode = 'recommend' | 'autonomous';

export default function Operator() {
  const [actions, setActions] = useState<OperatorAction[]>([]);
  const [digest, setDigest] = useState<Awaited<ReturnType<typeof api.operatorDigest>> | null>(null);
  const [mode, setMode] = useState<Mode>('recommend');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.allSettled([api.operatorActions(), api.operatorDigest()])
      .then(([a, d]) => {
        if (a.status === 'fulfilled') setActions(a.value);
        if (d.status === 'fulfilled') setDigest(d.value);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const fresh = await api.runOperator(mode);
      setActions((prev) => [...fresh, ...prev]);
      const d = await api.operatorDigest().catch(() => null);
      if (d) setDigest(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Operator run failed.');
    } finally {
      setRunning(false);
    }
  }

  const pending = actions.filter((a) => a.state === 'pending');
  const applied = actions.filter((a) => a.state === 'applied');

  return (
    <>
      <p className="eyebrow">Tier 3 · Autonomy</p>
      <h1 className="pg-title">AI Operator</h1>
      <p className="pg-sub">
        Runs the daily loop you'd otherwise do by hand: read yesterday's numbers, shift budget to
        what's working, pause what isn't, and queue replacements. Every change is reversible for 24
        hours.
      </p>

      <div className="card card-p run-panel">
        <div className="mode-picker">
          <label className={`mode ${mode === 'recommend' ? 'on' : ''}`}>
            <input
              type="radio"
              name="mode"
              checked={mode === 'recommend'}
              onChange={() => setMode('recommend')}
            />
            <div>
              <strong>Proposes changes</strong>
              <span>Nothing moves until you approve it. Start here.</span>
            </div>
          </label>
          <label className={`mode ${mode === 'autonomous' ? 'on' : ''}`}>
            <input
              type="radio"
              name="mode"
              checked={mode === 'autonomous'}
              onChange={() => setMode('autonomous')}
            />
            <div>
              <strong>Applies changes</strong>
              <span>
                Acts within the guardrails — ±30% budget, $100/day cap, no campaign deletions.
              </span>
            </div>
          </label>
        </div>

        <button className="btn brand" onClick={() => void run()} disabled={running}>
          {running ? 'Analysing your account…' : 'Run the operator now'}
        </button>
        {error && <p className="err-line">{error}</p>}
      </div>

      {digest && (
        <div className="card card-p">
          <h4>
            Digest <span className="meta">last 7 days</span>
          </h4>
          <p className="digest">{digest.summary}</p>
          <div className="grid-3">
            <div className="metric">
              <div className="lbl">Applied</div>
              <div className="val">{digest.actions.applied}</div>
            </div>
            <div className="metric">
              <div className="lbl">Awaiting you</div>
              <div className="val">{digest.actions.pending}</div>
            </div>
            <div className="metric">
              <div className="lbl">Reverted</div>
              <div className="val">{digest.actions.reverted}</div>
              <div className="delta flat">
                {digest.actions.reverted === 0 ? 'nothing rolled back' : 'rolled back by you'}
              </div>
            </div>
          </div>
          {digest.topResults.length > 0 && (
            <ul className="result-list">
              {digest.topResults.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <div className="card card-p">
          <h4>
            Waiting on you <span className="meta">{pending.length}</span>
          </h4>
          <ActionTable rows={pending} />
        </div>
      )}

      <div className="card card-p">
        <h4>
          Action ledger <span className="meta">every change, with rationale</span>
        </h4>
        {loading ? (
          <div className="empty">Loading…</div>
        ) : applied.length === 0 ? (
          <div className="empty">
            No actions yet. Run the operator in "proposes changes" mode to see what it would do
            without risking anything.
          </div>
        ) : (
          <ActionTable rows={applied} />
        )}
      </div>
    </>
  );
}

function ActionTable({ rows }: { rows: OperatorAction[] }) {
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Action</th>
          <th>Target</th>
          <th className="num">Change</th>
          <th>Why</th>
          <th>When</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.id}>
            <td>
              <span className={`badge b-${a.kind.split('_')[0]}`}>{a.kind.replace(/_/g, ' ')}</span>
            </td>
            <td className="mono">{a.target}</td>
            <td className={`num ${(a.delta ?? 0) >= 0 ? 'ok' : 'err'}`}>
              {a.delta == null ? '—' : fmt.delta(a.delta)}
            </td>
            <td className="rationale">{a.rationale}</td>
            <td className="meta">{fmt.relativeTime(a.applied_at ?? a.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
